import React, { useCallback, useEffect, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Chip, ClassNameMap, Tooltip, Typography } from '@mui/material';
import { IconButton } from '@mui/material';
import CancelSharpIcon from '@mui/icons-material/CancelSharp';
import { useResizeDetector } from 'react-resize-detector';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { useIntl } from 'react-intl';
import { CustomLabel } from 'modules/timesheet-labels/labelSchema';
import { LabelValueAcceptedBy, LabelValueSource } from 'graphql/types';
import { useFeatureFlag } from 'modules/common/hooks';
import { useIsSuggestion } from 'modules/common/hooks/useIsSuggestion';
import { useMessages } from 'modules/intl';

type CustomLabelProps = {
  label: CustomLabel;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onDelete?: () => void;
  setWidth?: (labelId: string, width: number) => void;
  disabled?: boolean;
  overrideClasses?: ClassNameMap;
  onAcceptSuggestion?: (customLabel: CustomLabel) => void;
};

type chipStyleProps = {
  classes?: ClassNameMap;
  isSuggestion?: boolean;
  hasDelete?: boolean;
};
const useStyles = makeStyles((theme) => ({
  root: {},
  chip: {
    display: 'flex',
    maxWidth: theme.spacing(60),
    height: theme.spacing(3),
    border: ({ isSuggestion }: chipStyleProps) =>
      isSuggestion
        ? `${theme.spacing(0.15)} dashed ${
            theme.palette.timesheetLabel.customLabelChipColor
          }`
        : `${theme.spacing(0.15)} solid ${
            theme.palette.timesheetLabel.customLabelChipColor
          }`,
    background: ({ isSuggestion }: chipStyleProps) =>
      isSuggestion
        ? 'transparent'
        : theme.palette.timesheetLabel.customLabelChipColor,
    margin: theme.spacing(0.5),
    cursor: 'pointer',
    color: ({ isSuggestion }: chipStyleProps) =>
      isSuggestion
        ? theme.palette.common.black
        : theme.palette.timesheetLabel.chipClickableColor,
    '&:hover': {
      background: ({ isSuggestion, hasDelete }: chipStyleProps) =>
        hasDelete
          ? isSuggestion
            ? theme.palette.grey[50]
            : theme.palette.timesheetLabel.customLabelChipColor
          : undefined
    }
  },
  text: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(0.125)
  },
  keyText: {
    padding: theme.spacing(0, 0, 0, 0)
  },
  labelText: {
    padding: theme.spacing(0, 0, 0, 0.4)
  },
  icon: {
    fontSize: 'small',
    position: 'absolute',
    left: 0,
    margin: theme.spacing(0, 0.625, 0, -0.75),
    color: theme.palette.timesheetLabel.chipClickableColor,
    opacity: 0.8,
    '&:hover': {
      opacity: 1
    }
  },
  suggestionAccept: {
    color: theme.palette.success.dark,
    fontSize: 'medium'
  },
  suggestionReject: {
    color: theme.palette.error.dark,
    fontSize: 'medium'
  }
}));

const message = [
  'timesheetLabels.acceptSuggestion',
  'timesheetLabels.denySuggestion',
  'timesheetLabels.removeBtn'
];

export const CustomLabelChip: React.FC<CustomLabelProps> = ({
  label,
  onClick,
  onDelete,
  setWidth,
  overrideClasses,
  disabled,
  onAcceptSuggestion
}: CustomLabelProps) => {
  const [focusElement, setFocusElement] = useState({
    isFocus: false,
    width: 0
  });

  const { width, ref: labelRef } = useResizeDetector();
  const isLabelLevelAcceptedByEnabled = useFeatureFlag(
    'isLabelLevelAcceptedByEnabled',
    false
  );
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );

  const isSuggestion = useIsSuggestion(label);
  const classes = useStyles({
    isSuggestion,
    classes: overrideClasses,
    hasDelete: Boolean(onDelete)
  });
  const mouseEnter = useCallback(() => {
    setFocusElement({
      isFocus: true,
      width: width ?? 0
    });
  }, [width]);
  const mouseLeave = useCallback(() => {
    setFocusElement({ isFocus: false, width: 0 });
  }, []);

  const onChipDelete = useMemo(() => {
    return focusElement.isFocus && !disabled && onDelete
      ? {
          onDelete: onDelete
        }
      : null;
  }, [disabled, focusElement.isFocus, onDelete]);
  const handleAcceptSuggestion = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onAcceptSuggestion?.({
        ...label,
        ...(isTimeSignalFederatedSuggestionsEnabled
          ? {
              value: label.suggestedValues?.[0]
            }
          : {}),
        valueSource: isLabelLevelAcceptedByEnabled
          ? label.valueSource ?? LabelValueSource.User
          : LabelValueSource.User,
        acceptedBy: LabelValueAcceptedBy.User
      });
    },
    [
      onAcceptSuggestion,
      label,
      isTimeSignalFederatedSuggestionsEnabled,
      isLabelLevelAcceptedByEnabled
    ]
  );

  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const messageLabel = useMessages(message);

  const deleteIcon = useMemo(
    () =>
      isSuggestion ? (
        <>
          <IconButton
            size="small"
            data-testid={`accept-icon-button-${label.id}`}
            onClick={handleAcceptSuggestion}
            aria-label={
              isTiwScreenReaderEnabled
                ? messageLabel['timesheetLabels.acceptSuggestion']
                : undefined
            }
          >
            <CheckCircleOutlineOutlinedIcon
              className={classes.suggestionAccept}
            />
          </IconButton>
          <IconButton
            size="small"
            data-testid={`reject-icon-button-${label.id}`}
            onClick={onDelete}
            aria-label={
              isTiwScreenReaderEnabled
                ? messageLabel['timesheetLabels.denySuggestion']
                : undefined
            }
          >
            <RemoveCircleOutlineOutlinedIcon
              className={classes.suggestionReject}
            />
          </IconButton>
        </>
      ) : (
        <IconButton
          data-testid="custom-label-delete-btn"
          size="small"
          aria-label={
            isTiwScreenReaderEnabled
              ? messageLabel['timesheetLabels.removeBtn']
              : undefined
          }
        >
          <CancelSharpIcon className={classes.icon} />
        </IconButton>
      ),
    [
      classes.icon,
      classes.suggestionAccept,
      classes.suggestionReject,
      handleAcceptSuggestion,
      isSuggestion,
      isTiwScreenReaderEnabled,
      label.id,
      messageLabel,
      onDelete
    ]
  );
  const onFocusChipWidth = useMemo(() => {
    return focusElement.isFocus
      ? { style: { width: focusElement.width } }
      : null;
  }, [focusElement]);
  const hoverForDeleteProps = useMemo(
    () =>
      disabled
        ? {}
        : {
            onMouseEnter: mouseEnter,
            onMouseLeave: mouseLeave,
            onFocus: mouseEnter,
            ...onFocusChipWidth
          },

    [disabled, mouseEnter, mouseLeave, onFocusChipWidth]
  );
  const tooltipText = useMemo(
    () => `${label.name}: ${label.value?.stringValue}`,
    [label]
  );
  const chipLabel = useMemo(
    () => (
      <div className={classes.text}>
        <Typography
          variant="caption"
          className={classes.keyText}
          data-testid="custom_Label_Key"
          noWrap
        >
          {label.name}
        </Typography>
        <Typography className={classes.keyText} variant="caption">
          :
        </Typography>
        <Typography
          variant="caption"
          className={classes.labelText}
          noWrap
          data-testid="custom_Label_Value"
        >
          {label.value?.stringValue}
        </Typography>
      </div>
    ),
    [
      classes.text,
      classes.keyText,
      classes.labelText,
      label.name,
      label.value?.stringValue
    ]
  );

  useEffect(() => {
    if (setWidth) {
      setWidth(label.id, (width ?? 0) + 4);
    }
  }, [label.id, label.name, setWidth, width]);

  const { formatMessage } = useIntl();
  const customButtonMsg = formatMessage(
    {
      id: 'timesheetLabels.editCustomLabel'
    },
    { value: `${label.name} ${label.value?.stringValue}` }
  );

  return (
    <div {...hoverForDeleteProps} className={classes.root} ref={labelRef}>
      <Tooltip
        title={tooltipText}
        disableInteractive
        data-testid="customLabel_key_tooltip"
      >
        <Chip
          data-testid="custom-label-chip"
          label={chipLabel}
          variant={isSuggestion ? 'outlined' : 'filled'}
          className={classes.chip}
          deleteIcon={deleteIcon}
          onClick={onClick}
          clickable={!disabled && Boolean(onClick)}
          {...onChipDelete}
          aria-label={customButtonMsg}
        />
      </Tooltip>
    </div>
  );
};
